import { sendAnalytics } from '@audio-network/analytics'
import { Divider, Heading, Paragraph } from '@audio-network/componentlibrary'
import {
	Close,
	Explicit,
	Info,
	Minus,
	MusicalNote,
	Plus,
	Terms,
	Vocals,
} from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useId, useRef, useState } from 'react'

import TrackFeaturedIn from '@/components/shared/TrackActions/TrackTray/TrackFeaturedIn/TrackFeaturedIn'
import CopyButton from '@/components/shared/TrackCueSheet/CopyButton/CopyButton'
import TrackCueSheet from '@/components/shared/TrackCueSheet/TrackCueSheet'
import { buildCueSheet } from '@/components/shared/TrackCueSheet/TrackCueSheet.utils'
import TrackMusicalFeatures from '@/components/shared/TrackMusicalFeatures/TrackMusicalFeatures'
import { getTrackUrl } from '@/utils/urls'

import styles from './TrackTray.module.css'
import { TrackTrayProps } from './TrackTray.types'
import TrackTrayHeader from './TrackTrayHeader/TrackTrayHeader'

type TrackTrayCollapsibleRegion = 'cueSheet' | 'lyrics' | 'musicalFeatures'

const initialExpanded: Record<TrackTrayCollapsibleRegion, boolean> = {
	cueSheet: true,
	lyrics: false,
	musicalFeatures: false,
}

const CollapsibleToggleIcons = (): JSX.Element => (
	<span className={styles.iconWrapper}>
		<Plus className={styles.toggleIconPlus} aria-hidden />
		<Minus className={styles.toggleIconMinus} aria-hidden />
	</span>
)

const TrackTray = ({
	className,
	track,
	source,
	...props
}: TrackTrayProps): JSX.Element => {
	const trayRef = useRef<HTMLDialogElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const traySectionId = useId()
	const cueSheetToggleId = `${traySectionId}-cue-sheet-toggle`
	const cueSheetRegionId = `${traySectionId}-cue-sheet-region`
	const lyricsToggleId = `${traySectionId}-lyrics-toggle`
	const lyricsRegionId = `${traySectionId}-lyrics-region`
	const musicalFeaturesToggleId = `${traySectionId}-musical-features-toggle`
	const musicalFeaturesRegionId = `${traySectionId}-musical-features-region`
	const [expanded, setExpanded] =
		useState<Record<TrackTrayCollapsibleRegion, boolean>>(initialExpanded)

	const { t } = useTranslation(['app'])
	const { t: tTrackrow } = useTranslation(['trackrow'])

	const { lyrics } = track

	const router = useRouter()

	const showLyricsSection = !!track.lyrics

	const toggleRegion = (key: TrackTrayCollapsibleRegion): void => {
		setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
	}

	const toggleTray = (): void => {
		sendAnalytics({
			noun: 'Track Tray',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackId: track.id,
				TrackTitle: track.title,
				AlbumNo: track.album.number,
				TrackSource: source,
				Genre: track.album.genre,
				Catalogue: track.album.catalogue.name,
			},
		})
		/**
		 * If the browser does not support dialogs, redirect to the track page
		 */
		const supportsDialog =
			typeof window !== 'undefined' &&
			'showModal' in window.HTMLDialogElement.prototype

		if (!supportsDialog) {
			const trackUrl = getTrackUrl(track)
			router.push(trackUrl)
			return
		}

		if (trayRef.current?.open) {
			trayRef.current?.close()
		} else {
			trayRef.current?.showModal()
		}
	}

	/**
	 * Safari omits `closedby`, so modal dialogs do not light-dismiss on backdrop.
	 */
	useEffect(() => {
		const dialog = trayRef.current
		if (dialog === null) {
			return
		}

		const handleBackdropClick = (event: MouseEvent): void => {
			if (event.target !== event.currentTarget) {
				return
			}
			dialog.close()
		}

		dialog.addEventListener('click', handleBackdropClick)
		return (): void => {
			dialog.removeEventListener('click', handleBackdropClick)
		}
	}, [])

	return (
		<>
			<Info
				className={clsx(className, styles.button)}
				data-track-info={track.id}
				title={tTrackrow('track-actions.track-tray')}
				onClick={toggleTray}
				{...props}
			/>
			<dialog
				{...({
					closedby: 'any',
				} as React.DialogHTMLAttributes<HTMLDialogElement>)}
				className={styles.tray}
				ref={trayRef}
			>
				<div className={styles.content} ref={contentRef}>
					<TrackTrayHeader track={track} />

					<section className={styles.section}>
						<Divider className={styles.divider} />
						<button
							type="button"
							id={cueSheetToggleId}
							className={styles.iconWrapperButton}
							aria-expanded={expanded.cueSheet}
							aria-controls={cueSheetRegionId}
							onClick={() => toggleRegion('cueSheet')}
						>
							<Heading className={styles.sectionHeading} level={3} size="XS">
								<Terms /> {t('cue-sheet')}
							</Heading>

							<CollapsibleToggleIcons />
						</button>

						<div
							role="region"
							aria-labelledby={cueSheetToggleId}
							aria-hidden={!expanded.cueSheet}
							className={styles.collapsibleRegion}
							id={cueSheetRegionId}
						>
							<div className={styles.collapsibleRegionContent}>
								<CopyButton
									data-testid="cue-sheet-copy-all-button"
									buttonType="copy-all"
									table={buildCueSheet(track)}
								/>
								<TrackCueSheet track={track} />
							</div>
						</div>
						<Divider className={styles.divider} />
					</section>

					{showLyricsSection ? (
						<section className={styles.section}>
							<button
								type="button"
								id={lyricsToggleId}
								className={styles.iconWrapperButton}
								aria-expanded={expanded.lyrics}
								aria-controls={lyricsRegionId}
								onClick={() => toggleRegion('lyrics')}
							>
								<Heading className={styles.sectionHeading} level={3} size="XS">
									<Vocals />
									{t('lyrics')}
									{track.explicit && (
										<Explicit title={tTrackrow('is-explicit')} />
									)}
								</Heading>

								<CollapsibleToggleIcons />
							</button>
							<div
								role="region"
								aria-labelledby={lyricsToggleId}
								aria-hidden={!expanded.lyrics}
								className={styles.collapsibleRegion}
								id={lyricsRegionId}
							>
								<div className={styles.collapsibleRegionContent}>
									<CopyButton
										data-testid="lyrics-copy-all-button"
										table={lyrics}
										buttonType="copy-lyrics"
									/>
									<Paragraph className={styles.lyricsText}>
										{track.lyrics}
									</Paragraph>
									<Paragraph className={styles.lyricsDisclaimer} size="S">
										{tTrackrow('lyrics-ai-disclaimer')}
									</Paragraph>
								</div>
							</div>
							<Divider className={styles.divider} />
						</section>
					) : null}

					<section className={styles.section}>
						<button
							type="button"
							id={musicalFeaturesToggleId}
							className={styles.iconWrapperButton}
							aria-expanded={expanded.musicalFeatures}
							aria-controls={musicalFeaturesRegionId}
							onClick={() => toggleRegion('musicalFeatures')}
						>
							<Heading className={styles.sectionHeading} level={3} size="XS">
								<MusicalNote />
								{t('musical-features')}
							</Heading>

							<CollapsibleToggleIcons />
						</button>
						<div
							role="region"
							aria-labelledby={musicalFeaturesToggleId}
							aria-hidden={!expanded.musicalFeatures}
							className={styles.collapsibleRegion}
							id={musicalFeaturesRegionId}
						>
							<div className={styles.collapsibleRegionContent}>
								<TrackMusicalFeatures track={track} />
							</div>
						</div>
						<Divider className={styles.divider} />
					</section>

					<section className={styles.section}>
						<Heading className={styles.sectionHeading} level={3} size="XS">
							{t('featured-in')}
						</Heading>
						<TrackFeaturedIn track={track} />
					</section>
				</div>
				<button
					aria-label={t('close')}
					className={styles.close}
					onClick={toggleTray}
				>
					<Close />
				</button>
			</dialog>
		</>
	)
}

export default TrackTray
